import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import RestaurantHeader from '../../components/navigation/RestaurantHeader';
import SearchBar from './components/SearchBar';
import CategoryFilter from './components/CategoryFilter';
import CategorySection from './components/CategorySection';
import EmptyState from './components/EmptyState';
import LoadingState from './components/LoadingState';
import PromotionsPopup from './components/PromotionsPopup';
import { useLanguage } from '../../i18n/LanguageContext';
import { useDishFilters } from '../../hooks/useDishFilters';
import { useDishTranslation } from '../../hooks/useDishTranslation';
import { calculateCategoryCounts, groupDishesByCategory, normalizeDishCategory } from '../../utils/dishHelpers';
import ErrorToast from '../../components/ui/ErrorToast';
import { trackQRScan } from '../../services/dishStatsService';

import { fetchPublicMenu, fetchRestaurantProfile, fetchCategories, fetchDishes } from '../../services/menuDataService';
import { fetchPromotions } from '../../services/promotionsService';
import { restaurantProfile as defaultRestaurantProfile } from '../../data/restaurantData';
import { useTenant } from '../../contexts/TenantContext';
import { dishesData } from '../../data/dishesData';


// Helper to generate unique toast IDs
let _toastId = 0;
const newToastId = () => `toast-${++_toastId}`;

const RestaurantMenuLanding = () => {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const { restaurantId, slug, isLoading: tenantLoading } = useTenant();
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');

  // Promotions popup state
  const [promotions, setPromotions] = useState([]);
  const [showPromotionsPopup, setShowPromotionsPopup] = useState(false);

  // Error toasts state
  const [errorToasts, setErrorToasts] = useState([]);
  const addToast = (message, type = 'error') =>
    setErrorToasts(prev => [...prev, { id: newToastId(), message, type }]);
  const dismissToast = (id) =>
    setErrorToasts(prev => prev?.filter(t => t?.id !== id));

  // ── Restaurant profile from Supabase ──────────────────────────────────────
  const [restaurantProfile, setRestaurantProfile] = useState(defaultRestaurantProfile);

  // ── Dishes from Supabase ──────────────────────────────────────────────────
  const [dishes, setDishes] = useState([]);

  // ── Categories from Supabase ──────────────────────────────────────────────
  const [adminCategories, setAdminCategories] = useState(null);

  useEffect(() => {
    if (tenantLoading) return;

    let cancelled = false;

    // No slug — either preview mode (no restaurantId) or direct restaurantId access
    if (!slug) {
      if (!restaurantId) {
        // Pure preview: show demo data
        setDishes(dishesData);
        setAdminCategories(null);
        setPromotions([]);
        setIsLoading(false);
        return;
      }

      // Admin preview via ?restaurant_id=xxx — load real data directly
      setIsLoading(true);
      Promise.all([
        fetchRestaurantProfile(restaurantId),
        fetchCategories(restaurantId),
        fetchDishes(restaurantId),
        fetchPromotions(false, restaurantId)?.catch(() => []),
      ])?.then(([profile, cats, dishList, promos]) => {
          if (cancelled) return;
          if (profile) {
            setRestaurantProfile({
              id: profile?.id,
              name: profile?.name,
              tagline: profile?.tagline,
              cuisine: profile?.cuisine,
              phone: profile?.phone,
              email: profile?.email,
              address: profile?.address,
              hours: profile?.hours,
              currency: profile?.currency,
              logo: profile?.logo,
              logoAlt: profile?.logoAlt,
              restaurantId: profile?.restaurantId,
            });
          }
          const mappedCats = (cats || [])?.map((cat) => ({
            id: cat?.id,
            name: cat?.name,
            sortOrder: cat?.sortOrder,
            itemCount: 0,
          }));
          setAdminCategories(mappedCats);
          setDishes((dishList || [])?.filter((d) => !d?.hidden));
          const mappedPromotions = (promos || [])
            ?.filter((p) => p?.active)
            ?.map((p) => ({
              id: p?.id,
              title: p?.title || '',
              description: p?.description || '',
              imageUrl: p?.image_url || p?.imageUrl || '',
              active: p?.active !== false,
            }));
          setPromotions(mappedPromotions);
          if (mappedPromotions?.length > 0) setShowPromotionsPopup(true);
        })?.catch(() => {
          if (!cancelled) addToast('Unable to load menu for this restaurant.', 'error');
        })?.finally(() => {
          if (!cancelled) setIsLoading(false);
        });

      return () => { cancelled = true; };
    }

    setIsLoading(true);

    fetchPublicMenu(slug)?.then((payload) => {
        if (cancelled || !payload) return;

        if (payload?.profile) {
          setRestaurantProfile({
            id: payload?.profile?.id,
            name: payload?.profile?.name,
            tagline: payload?.profile?.tagline,
            cuisine: payload?.profile?.cuisine,
            phone: payload?.profile?.phone,
            email: payload?.profile?.email,
            address: payload?.profile?.address,
            hours: payload?.profile?.hours,
            currency: payload?.profile?.currency,
            logo: payload?.profile?.logo,
            logoAlt: payload?.profile?.logo_alt,
            restaurantId: payload?.profile?.restaurant_id,
          });
        }

        const categories = (payload?.categories || [])?.map((cat) => ({
          id: cat?.id,
          name: cat?.name,
          sortOrder: cat?.sort_order,
          itemCount: 0,
        }));
        const mappedDishes = (payload?.dishes || [])?.map((dish) => ({
          id: dish?.id,
          name: dish?.name,
          description: dish?.description || '',
          price: parseFloat(dish?.price) || 0,
          originalPrice: dish?.original_price ? parseFloat(dish?.original_price) : null,
          category: normalizeDishCategory(dish?.category, categories),
          image: dish?.image || '',
          imageAlt: dish?.image_alt || '',
          ingredients: dish?.ingredients || [],
          calories: dish?.calories || 0,
          isNew: dish?.is_new || false,
          isPopular: dish?.is_popular || false,
          isVegetarian: dish?.is_vegetarian || false,
          spiceLevel: dish?.spice_level || 0,
          arUrl: dish?.ar_url || '',
          hidden: dish?.hidden || false,
          sortOrder: dish?.sort_order || 0,
          widthCm: dish?.width_cm ?? null,
          heightCm: dish?.height_cm ?? null,
          depthCm: dish?.depth_cm ?? null,
          chips: Array.isArray(dish?.chips) ? dish?.chips : [],
          restaurantId: dish?.restaurant_id,
        }));

        const mappedPromotions = (payload?.promotions || [])
          ?.filter((p) => p?.active)
          ?.map((p) => ({
            id: p?.id,
            title: p?.title || '',
            description: p?.description || '',
            imageUrl: p?.image_url || '',
            active: p?.active !== false,
          }));

        setAdminCategories(categories);
        setDishes(mappedDishes?.filter((d) => !d?.hidden));
        setPromotions(mappedPromotions);
        if (mappedPromotions?.length > 0) {
          setShowPromotionsPopup(true);
        }
      })?.catch(() => {
        if (!cancelled) addToast('Unable to load menu for this restaurant.', 'error');
      })?.finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [slug, tenantLoading, restaurantId]);

  // Build categories list
  const categories = useMemo(() => adminCategories
    ? [
        { id: 'all', name: t('allDishes'), description: '' },
        ...adminCategories?.map(cat => ({
          id: cat?.id,
          name: cat?.name,
          description: '',
        }))
      ]
    : [
        { id: 'all', name: t('allDishes'), description: '' },
        { id: 'appetizers', name: t('appetizers'), description: '' },
        { id: 'mains', name: t('mainCourse'), description: '' },
        { id: 'desserts', name: t('desserts'), description: '' },
        { id: 'beverages', name: t('beverages'), description: '' },
      ], [adminCategories, t]);

  // Use custom hook for filtering
  const filteredDishes = useDishFilters(dishes, activeCategory, searchQuery, categories);

  // Translate dishes when EN is selected
  const { translatedDishes } = useDishTranslation(filteredDishes, language, restaurantProfile?.restaurantId);

  // Calculate category counts
  const categoriesWithCounts = useMemo(() => calculateCategoryCounts(categories, dishes), [categories, dishes]);

  // Group dishes by category
  const groupedDishes = useMemo(() => groupDishesByCategory(translatedDishes, categories, activeCategory), [translatedDishes, categories, activeCategory]);

  // Track QR scan only when user enters via ?source=qr
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params?.get('source') === 'qr') {
      trackQRScan('restaurant', 'Restaurant QR Scan', restaurantId);
    }
  }, [restaurantId]);

  const handleViewAR = (dish) => {
    navigate('/ar-viewer', { state: { dish } });
  };

  const handleClearSearch = () => {
    setSearchQuery('');
  };

  if (isLoading || tenantLoading) {
    return (
      <div className="restaurant-menu-landing min-h-screen bg-background">
        <RestaurantHeader
          restaurantName={restaurantProfile?.name}
          restaurantLogo={restaurantProfile?.logo}
          tagline={restaurantProfile?.tagline}
        />
        <LoadingState />
      </div>
    );
  }

  return (
    <div className="restaurant-menu-landing min-h-screen bg-background">
      <RestaurantHeader
        restaurantName={restaurantProfile?.name}
        restaurantLogo={restaurantProfile?.logo}
        tagline={restaurantProfile?.tagline}
      />
      <main className="container mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8 lg:py-10">
        <div className="menu-controls-section mb-6 md:mb-8 space-y-4 md:space-y-6">
          <SearchBar
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            resultCount={filteredDishes?.length}
          />

          <CategoryFilter
            categories={categoriesWithCounts}
            activeCategory={activeCategory}
            onCategoryChange={setActiveCategory}
          />
        </div>

        <div className="menu-content-section">
          {filteredDishes?.length === 0 ? (
            <EmptyState
              searchQuery={searchQuery}
              onClearSearch={handleClearSearch}
            />
          ) : (
            <div className="menu-categories-container space-y-8 md:space-y-10 lg:space-y-12">
              {Object.entries(groupedDishes)?.map(([categoryId, categoryDishes]) => {
                const category = categories?.find(cat => cat?.id === categoryId);
                return (
                  <CategorySection
                    key={categoryId}
                    category={category}
                    dishes={categoryDishes}
                    onViewAR={handleViewAR}
                    onViewDetails={handleViewAR}
                  />
                );
              })}
            </div>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="w-full py-10 mt-8 border-t border-border bg-card">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <div className="flex flex-col items-center gap-2 text-center mb-6">
            <h2 className="text-xl font-bold text-foreground">{restaurantProfile?.name}</h2>
            {restaurantProfile?.tagline && (
              <p className="text-sm italic text-muted-foreground">{restaurantProfile?.tagline}</p>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-center max-w-2xl mx-auto">
            {restaurantProfile?.phone && (
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">{t('footerPhone')}</p>
                <p className="text-sm text-foreground">{restaurantProfile?.phone}</p>
              </div>
            )}
            {restaurantProfile?.email && (
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">{t('footerEmail')}</p>
                <p className="text-sm text-foreground">{restaurantProfile?.email}</p>
              </div>
            )}
            {restaurantProfile?.address && (
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">{t('footerAddress')}</p>
                <p className="text-sm text-foreground">{restaurantProfile?.address}</p>
              </div>
            )}
          </div>
          <div className="flex justify-center mt-6">
            <div className="flex flex-col items-center gap-1">
              <span className="text-xs font-semibold text-foreground tracking-widest">SCANN3R</span>
              <span className="text-xs text-foreground opacity-70">felix.gagnon@scann3r.ca</span>
              <button
                onClick={() => navigate('/restaurant-admin-dashboard')}
                className="text-xs text-muted-foreground hover:text-foreground transition-colors mt-1"
              >
                Admin
              </button>
              <button
                onClick={() => navigate('/super-admin')}
                className="text-xs text-muted-foreground hover:text-foreground transition-colors mt-1"
              >
                Platform Admin
              </button>
              <button
                onClick={() => navigate('/modalites-politiques')}
                className="text-xs text-muted-foreground hover:text-foreground transition-colors mt-1"
              >
                {t('footerTerms')}
              </button>
            </div>
          </div>
        </div>
      </footer>

      {/* Error Toasts */}
      <ErrorToast toasts={errorToasts} onDismiss={dismissToast} />

      {/* Promotions Popup */}
      {showPromotionsPopup && promotions?.length > 0 && (
        <PromotionsPopup
          promotions={promotions}
          onClose={() => setShowPromotionsPopup(false)}
        />
      )}
    </div>
  );
};

export default RestaurantMenuLanding;